/*
 * Copyright 2024 GLA UK Research and Development Directive
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { DomainService } from 'src/app/modules/shared/services/domain.service';
import { NiordKeycloakService } from 'src/app/modules/shared/services/niord-keycloak.service';
import { LanguageService } from 'src/app/modules/shared/services/language.service';
import { Domain } from 'src/app/modules/shared/models/domain';

// Icons
import { faCheck, faEye, faPencilAlt, faUser } from '@fortawesome/free-solid-svg-icons';

/**
 * The Header Bar Component.
 */
@Component({
    selector: 'header-bar',
    templateUrl: './header-bar.component.html',
    styleUrls: ['./header-bar.component.scss'],
    standalone: false
})
export class HeaderBarComponent implements OnInit {

    // Class Variables
    public mode: string = "development";
    public domain: Domain = undefined;
    public domains: any = [];
    public language: string = "";
    public siteLanguages: string[] = [];

    // User Information
    public isLoggedIn: boolean = false;
    public userName: string = "";
    public userDesc: string = "";

    // Icons
    faCheck = faCheck;
    faPencilAlt = faPencilAlt;
    faEye = faEye;
    faUser = faUser;

    /**
     * The Header Bar Component Constructor.
     * 
     * @param niordKeycloak     The Niord keycloak service
     * @param route             The angular active route
     * @param router            The angular router
     * @param domainService     The domain service
     * @param languageService   The language service
     */
    constructor(protected niordKeycloak: NiordKeycloakService,
                protected route: ActivatedRoute,
                protected router: Router,
                protected domainService: DomainService,
                protected languageService: LanguageService) {
        this.domain = domainService.getDomain();
        this.domains = domainService.getStoredDomains();
        this.language = languageService.getLanguage();
        this.siteLanguages = environment.siteLanguages;
    }

    /**
     * The Angular On Init function.
     */
    ngOnInit(): void {
        this.niordKeycloak.isLoggedIn()
            .then(loggedIn => this.isLoggedIn = loggedIn)
            .catch(() => this.isLoggedIn = false);
        this.niordKeycloak.getUserName()
            .then(name => this.userName = name)
            .catch(() => this.userName = "");
        this.niordKeycloak.getUserDesc()
            .then(desc => this.userDesc = desc)
            .catch(() => this.userDesc = "");
    }

    /**
     * Changes the current domain of the user.
     * 
     * @param domain    The domain to switch to
     */
    changeDomain(domain: Domain) {
        // Change domain
        this.domainService.changeDomain(domain);
        // Reset all parameters
        this.router.navigate(['.'], { relativeTo: this.route, queryParams: {} });
    }

    /**
     * Changes the current display language for the user.
     * 
     * @param language  The language to switch to
     */
    changeLanguage(language: string) {
        this.language = language;
        this.languageService.changeLanguage(language);
    }

    /**
     * Returns the style of the navbar based on the domain selection.
     */
    getNavbarStyle() {
        if (this.domain && this.domain.color) {
            return { 'border-bottom': `1px solid ${this.domain.color}` };
        }

        return { 'border-bottom': "0px" };
    }

    /****************************************************************************
     *                            Function Hooks
     ****************************************************************************/
    /**
     * Uses the Niord keycloak service to perform the user login.
     */
    login() {
        this.niordKeycloak.login();
    };

    /**
     * Uses the Niord keycloak service to perform the user logout.
     */
    logout() {
        this.niordKeycloak.logout();
    };

    /**
     * Uses the Niord keycloak service to check whether the user has a specific
     * role.
     * 
     * @param role    The role to be checked if belonging to the user
     */
    hasRole(role: string) {
        return this.niordKeycloak.hasRole(role);
    };

    /**
     * Uses the Niord keycloak service to direct the user to the keycloak account
     * management page.
     */
    accountManagement() {
        this.niordKeycloak.accountManagement();
    };
    /****************************************************************************/

}
